// V9.25 · Contas a Receber + inadimplência do mês
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, GraduationCap } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const URG = {
  vencido: ['Vencido', 'bg-rose-100 text-rose-800 border-rose-300'],
  hoje: ['Vence hoje', 'bg-rose-50 text-rose-700 border-rose-200'],
  sete_dias: ['Em 7 dias', 'bg-amber-100 text-amber-800 border-amber-300'],
  a_vencer: ['A vencer', 'bg-slate-100 text-slate-700 border-slate-300'],
};

export const ReceberTab = ({ competencia }) => {
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/fin/ledger/receivables', { params: { competencia } });
      setData(d);
    } catch (_) { toast.error('Erro ao carregar contas a receber.'); }
    finally { setLoading(false); }
  }, [competencia]);

  useEffect(() => { load(); }, [load]);

  const b = data?.por_urgencia || {};
  const inad = data?.inadimplencia || {};
  const itens = (data?.itens || []).filter((i) => filtro === 'all' || i.urgencia === filtro);

  return (
    <div className="space-y-3" data-testid="tab-contas-receber">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[['vencido', 'Vencidas'], ['hoje', 'Vencem hoje'], ['sete_dias', 'Em até 7 dias'],
          ['a_vencer', 'A vencer']].map(([k, label]) => (
          <button key={k} type="button" data-testid={`receber-bucket-${k}`}
            onClick={() => setFiltro(filtro === k ? 'all' : k)}
            className={`text-left rounded-xl border p-3 transition-transform hover:-translate-y-0.5 ${
              filtro === k ? 'ring-2 ring-primary' : ''} ${URG[k][1]}`}>
            <p className="text-[11px] font-bold uppercase tracking-wide">{label}</p>
            <p className="text-lg font-bold tabular-nums">{money(b[k])}</p>
          </button>
        ))}
        <div className="rounded-xl border p-3 bg-foreground/5">
          <p className="text-[11px] font-bold uppercase text-muted-foreground">Total a receber</p>
          <p className="text-lg font-bold tabular-nums" data-testid="receber-total">{money(data?.total_aberto)}</p>
        </div>
        <div className="rounded-xl border p-3 border-rose-300 bg-rose-50/70" data-testid="card-inadimplencia">
          <p className="text-[11px] font-bold uppercase text-muted-foreground flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Inadimplência {data?.competencia}
          </p>
          <p className="text-lg font-bold tabular-nums text-rose-700">{money(inad.valor)}</p>
          <p className="text-[11px] text-muted-foreground">
            {inad.percent ?? 0}% da base · {inad.titulos ?? 0} título(s)
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="w-4 h-4" /> Previsto a receber
            {filtro !== 'all' && <Badge variant="outline" className="ml-1">{URG[filtro][0]}</Badge>}
          </CardTitle>
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground">
              {itens.length} de {data?.total_itens || 0}
            </p>
            {filtro !== 'all' && (
              <Button size="sm" variant="ghost" onClick={() => setFiltro('all')}
                data-testid="receber-limpar-filtro">Ver todas</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Vencimento</TableHead><TableHead>Aluno / descrição</TableHead>
              <TableHead>Turma</TableHead><TableHead>Origem</TableHead>
              <TableHead className="text-right">Valor</TableHead><TableHead>Situação</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8">Carregando…</TableCell></TableRow>}
              {!loading && !itens.length && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  Nada a receber neste filtro.
                </TableCell></TableRow>
              )}
              {!loading && itens.map((r) => (
                <TableRow key={r.id} data-testid={`receber-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-xs font-medium">{r.due_date || '—'}</TableCell>
                  <TableCell className="max-w-[280px]">
                    <p className="truncate font-medium">{r.aluno || r.descricao}</p>
                    {r.aluno && <p className="text-[11px] text-muted-foreground truncate">{r.descricao}</p>}
                  </TableCell>
                  <TableCell className="text-xs">{r.turma || '—'}</TableCell>
                  <TableCell className="text-xs">
                    {r.origem === 'titulo' ? 'Título do aluno' : 'Receita prevista'}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{money(r.valor)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={URG[r.urgencia][1]}>{URG[r.urgencia][0]}</Badge>
                    {r.dias_para_vencer < 0 && (
                      <span className="ml-1 text-[11px] text-rose-700">
                        {Math.abs(r.dias_para_vencer)}d
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReceberTab;
